import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getNotifications,
  getNotificationStats,
  getNotificationById,
  sendNotificationAnyway,
  bulkUpdateNotificationStatus,
  exportNotificationsCsv,
  updateNotificationTags,
  getNotificationTemplates,
} from "../services/get-notifications";
import { toast } from "@/hooks/use-toast";
import { Notification } from "@/pages/notifications/types/notification";

interface NotificationsQueryData {
  notifications: Notification[];
  currentPage: number;
  lastPage: number;
  totalCount: number;
}

export const useNotifications = ({
  page = 0,
  search = "",
  status,
  startDate,
  endDate,
  tags,
  template,
  type,
}: {
  page: number;
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  tags?: boolean;
  template?: string;
  type?: string;
}) => {
  const { data, isLoading, error, isFetching, isSuccess } = useQuery({
    queryKey: [
      "notifications",
      {
        page: page,
        search: search,
        status: status,
        startDate,
        endDate,
        tags,
        template,
        type,
      },
    ],
    queryFn: () =>
      getNotifications({
        page,
        search,
        status,
        startDate,
        endDate,
        tags,
        template,
        type,
      }),
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
  });
  return { data, isLoading, error, isFetching, isSuccess };
};

export const useNotificationTemplates = () => {
  return useQuery({
    queryKey: ["notification-templates"],
    queryFn: getNotificationTemplates,
    staleTime: 1000 * 60 * 60, // 1 hour — template list rarely changes
    refetchOnWindowFocus: false,
  });
};

export const useNotification = (id: string) => {
  const { data, isLoading, error, isFetching, isSuccess } = useQuery({
    queryKey: [
      "notification-detail",
      {
        id,
      },
    ],
    queryFn: () => getNotificationById(id),
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
  });

  return { data, isLoading, error, isFetching, isSuccess };
};

export const useNotificationStats = () => {
  return useQuery({
    queryKey: ["notification-stats"],
    queryFn: getNotificationStats,
    staleTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false,
  });
};

export const useSendNotificationAnyway = (
  onStatusChange?: (id: string, status: string) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      notificationId,
    }: {
      sessionId: string;
      notificationId: string;
    }) => sendNotificationAnyway(sessionId, notificationId),

    onMutate: async ({ notificationId }) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      const previousData = queryClient.getQueryData<any>(["notifications"]);

      queryClient.setQueryData<any>(["notifications"], (oldData: any) => {
        if (!oldData) return oldData;

        const updatedNotifications = oldData.notifications.map((n: any) =>
          n.id === notificationId ? { ...n, status: "SENT" } : n
        );

        return { ...oldData, notifications: updatedNotifications };
      });

      return { previousData };
    },

    onError: (error: any, _variables, context) => {
      queryClient.setQueryData(["notifications"], context?.previousData);

      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "Failed to send notification",
        variant: "destructive",
      });
    },

    onSuccess: (_data, variables) => {
      toast({
        title: "Success",
        description: "Notification added to queue successfully",
      });

      onStatusChange?.(variables.notificationId, "SENT");

      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notification-stats"] });
    },
  });
};

export const useBulkUpdateNotificationStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: string }) =>
      bulkUpdateNotificationStatus(ids, status),

    onSuccess: (_, { ids, status }) => {
      toast({
        title: "Success",
        description: "Notifications updated successfully",
      });

      queryClient
        .getQueryCache()
        .findAll({ queryKey: ["notifications"] })
        .forEach(({ queryKey }) => {
          queryClient.setQueryData(queryKey, (oldData: any) => {
            if (!oldData) return oldData;

            const updatedNotifications = oldData.notifications.map((n: any) =>
              ids.includes(n.id) ? { ...n, status } : n
            );

            return { ...oldData, notifications: updatedNotifications };
          });
        });

      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },

    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "Failed to update notifications",
        variant: "destructive",
      });
    },
  });
};

export const useExportNotificationsCsv = () => {
  return useMutation({
    mutationFn: exportNotificationsCsv,

    onSuccess: (data) => {
      const blob = new Blob([data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "notifications.csv";
      link.click();

      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "CSV exported successfully",
      });
    },

    onError: () => {
      toast({
        title: "Error",
        description: "Failed to export CSV",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateNotificationTags = (
  onUpdated?: (ids: string[]) => void
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      notificationId,
      tags,
    }: {
      notificationId: string;
      tags: string[];
    }) => updateNotificationTags(notificationId, tags),

    onMutate: async ({ notificationId, tags }) => {
      await queryClient.cancelQueries({ queryKey: ["notifications"] });

      const previousQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: ["notifications"] })
        .map((query) => ({
          queryKey: query.queryKey,
          data: query.state.data,
        }));

      const updateNotification = (n: Notification) => {
        if (
          n.id === notificationId ||
          n.linked_notification_id === notificationId ||
          n.linked_to_notifications?.some(
            (ln: Notification) => ln.id === notificationId
          )
        ) {
          return { ...n, tags };
        }
        return n;
      };

      previousQueries.forEach(({ queryKey }) => {
        queryClient.setQueryData<NotificationsQueryData | undefined>(
          queryKey,
          (oldData) =>
            oldData
              ? {
                  ...oldData,
                  notifications: oldData.notifications.map(updateNotification),
                }
              : oldData
        );
      });

      // Detail cache
      queryClient.setQueryData<Notification | undefined>(
        ["notification-detail", { id: notificationId }],
        (old) =>
          old?.recipient_type === "PROFESSIONAL"
            ? { ...old, tags }
            : old
      );

      return { previousQueries };
    },

    onError: (error, _variables, context) => {
      context?.previousQueries.forEach(({ queryKey, data }) => {
        queryClient.setQueryData(queryKey, data);
      });

      toast({
        title: "Error",
        description:
          (error as any)?.response?.data?.message ||
          "Failed to update notification tags",
        variant: "destructive",
      });
    },

    onSuccess: (_data, variables) => {
      toast({
        title: "Success",
        description: "Notification tags updated",
      });

      onUpdated?.([variables.notificationId]);
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-stats"] });
    },
  });
};
